import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Search, Phone, Copy, Check, Loader2, ChevronLeft, ChevronRight, RefreshCw, History, IndianRupee, Download, FileText, Edit, AlertTriangle } from 'lucide-react';

export default function ZabhiyatTab() {
  const { api, isAdmin, isFMBViewer, isZabhiyatRole, canExportPDF, canExportExcel } = useAuth();
  
  // State
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({});
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  
  // Filters
  const [search, setSearch] = useState('');
  const [selectedMohalla, setSelectedMohalla] = useState('all');
  const [selectedSector, setSelectedSector] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [filterType, setFilterType] = useState(''); // For done_but_unpaid filter
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Dialogs
  const [markDoneDialog, setMarkDoneDialog] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [auditDialog, setAuditDialog] = useState(null);
  const [resetDialog, setResetDialog] = useState(null);  // New: Reset/Edit dialog
  const [confirmUnpaidDialog, setConfirmUnpaidDialog] = useState(null);  // New: Confirm unpaid marking
  const [doneCount, setDoneCount] = useState('');
  const [paidCount, setPaidCount] = useState('');
  const [resetCount, setResetCount] = useState('');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  
  // Clipboard
  const [copiedIts, setCopiedIts] = useState(null);

  // Fetch dashboard stats
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get('/fmb/zabhiyat/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    }
  }, [api]);

  // Fetch Zabhiyat list
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '50');
      if (search) params.append('search', search);
      if (selectedMohalla && selectedMohalla !== 'all') params.append('mohalla', selectedMohalla);
      if (selectedSector && selectedSector !== 'all') params.append('sector', selectedSector);
      if (paymentFilter && paymentFilter !== 'all') params.append('payment_status', paymentFilter);
      if (pendingOnly) params.append('pending_only', 'true');
      
      const response = await api.get(`/fmb/zabhiyat?${params}`);
      setData(response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch Zabhiyat data');
    } finally {
      setLoading(false);
    }
  }, [api, page, search, selectedMohalla, selectedSector, paymentFilter, pendingOnly]);

  // Fetch mohallas
  const fetchMohallas = useCallback(async () => {
    try {
      const response = await api.get('/mohallas');
      setMohallas(response.data?.mohallas || response.data || []);
    } catch (error) {
      console.error('Mohallas fetch error:', error);
    }
  }, [api]);

  // Fetch sectors
  const fetchSectors = useCallback(async () => {
    try {
      const params = (selectedMohalla && selectedMohalla !== 'all') ? `?mohalla=${encodeURIComponent(selectedMohalla)}` : '';
      const response = await api.get(`/sectors${params}`);
      setSectors(response.data?.sectors || response.data || []);
    } catch (error) {
      console.error('Sectors fetch error:', error);
    }
  }, [api, selectedMohalla]);

  useEffect(() => {
    fetchMohallas();
    fetchDashboard();
  }, [fetchMohallas, fetchDashboard]);

  useEffect(() => {
    fetchSectors();
  }, [selectedMohalla, fetchSectors]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [search, selectedMohalla, selectedSector, paymentFilter, pendingOnly]);

  // Copy mobile to clipboard
  const copyMobile = async (mobile, its) => {
    if (!mobile) {
      toast.error('No mobile number available');
      return;
    }
    try {
      await navigator.clipboard.writeText(mobile);
      setCopiedIts(its);
      toast.success('Mobile number copied');
      setTimeout(() => setCopiedIts(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  // Mark Zabhiyat done
  const handleMarkDone = async () => {
    if (!markDoneDialog || !doneCount) return;
    
    const count = parseInt(doneCount);
    if (isNaN(count) || count <= 0) {
      toast.error('Please enter a valid count');
      return;
    }
    
    if (count > markDoneDialog.zabhiyat_pending) {
      toast.error(`Cannot mark more than ${markDoneDialog.zabhiyat_pending} pending`);
      return;
    }
    
    // Check if unpaid - show confirmation for non-admin
    if (markDoneDialog.payment_status === 'Unpaid' && !isAdmin) {
      setConfirmUnpaidDialog({ item: markDoneDialog, count });
      return;
    }
    
    await executeMarkDone(markDoneDialog.hof_its, count);
  };

  // Execute mark done after confirmation
  const executeMarkDone = async (hof_its, count) => {
    try {
      setSaving(true);
      await api.post('/fmb/zabhiyat/mark-done', {
        hof_its: hof_its,
        done_count: count
      });
      toast.success(`Marked ${count} Zabhiyat as done`);
      setMarkDoneDialog(null);
      setConfirmUnpaidDialog(null);
      setDoneCount('');
      fetchData();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Reset/Edit Zabhiyat done (Admin only)
  const handleResetDone = async () => {
    if (!resetDialog || resetCount === '') return;
    
    const count = parseInt(resetCount);
    if (isNaN(count) || count < 0) {
      toast.error('Please enter a valid count');
      return;
    }
    
    if (count > resetDialog.zabhiyat_total) {
      toast.error(`Cannot exceed total (${resetDialog.zabhiyat_total})`);
      return;
    }
    
    try {
      setSaving(true);
      await api.post('/fmb/zabhiyat/reset-done', {
        hof_its: resetDialog.hof_its,
        new_done_count: count
      });
      toast.success(`Updated done count to ${count}`);
      setResetDialog(null);
      setResetCount('');
      fetchData();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Export handler
  const handleExport = async (format) => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      params.append('format', format);
      if (selectedMohalla && selectedMohalla !== 'all') params.append('mohalla', selectedMohalla);
      if (selectedSector && selectedSector !== 'all') params.append('sector', selectedSector);
      if (paymentFilter && paymentFilter !== 'all') params.append('payment_status', paymentFilter);
      if (filterType) params.append('filter_type', filterType);
      
      const response = await api.get(`/fmb/zabhiyat/export?${params}`, { responseType: 'blob' });
      
      const mimeType = format === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const fileName = `Zabhiyat_Report_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      toast.success(`${format.toUpperCase()} downloaded successfully`);
    } catch (error) {
      toast.error('Failed to export: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  // Handle dashboard card click (Admin only for done_but_unpaid)
  const handleDashboardClick = (type) => {
    if (type === 'done_but_unpaid' && isAdmin) {
      setFilterType('done_but_unpaid');
      setPaymentFilter('unpaid');
      setPage(1);
    }
  };

  // Clear filter
  const clearDoneButtUnpaidFilter = () => {
    setFilterType('');
    setPaymentFilter('all');
    setPage(1);
  };

  // Update payment (Admin only)
  const handleUpdatePayment = async () => {
    if (!paymentDialog || paidCount === '') return;
    
    const count = parseInt(paidCount);
    if (isNaN(count) || count < 0) {
      toast.error('Please enter a valid count');
      return;
    }
    
    if (count > paymentDialog.zabhiyat_total) {
      toast.error(`Cannot mark more than ${paymentDialog.zabhiyat_total} total`);
      return;
    }
    
    try {
      setSaving(true);
      await api.post('/fmb/zabhiyat/update-payment', {
        hof_its: paymentDialog.hof_its,
        paid_count: count
      });
      toast.success('Payment status updated');
      setPaymentDialog(null);
      setPaidCount('');
      fetchData();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Fetch audit log
  const fetchAuditLog = async (hof_its) => {
    try {
      const response = await api.get(`/fmb/zabhiyat/audit-log/${hof_its}`);
      setAuditHistory(response.data.history || []);
      setAuditDialog({ hof_its, hof_name: response.data.hof_name });
    } catch (error) {
      toast.error('Failed to fetch audit log');
    }
  };

  // Payment status badge
  const getPaymentBadge = (status) => {
    switch (status) {
      case 'Paid':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
      case 'Partial':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Partial</Badge>;
      case 'Unpaid':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Unpaid</Badge>;
      default:
        return <Badge variant="outline">N/A</Badge>;
    }
  };

  const canEdit = isAdmin || isZabhiyatRole;
  const canEditPayment = isAdmin;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="zabhiyat-tab">
      {/* Header with Export Buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          {filterType === 'done_but_unpaid' && (
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-purple-100 text-purple-700 text-xs">Done but Unpaid</Badge>
              <Button variant="ghost" size="sm" onClick={clearDoneButtUnpaidFilter} className="h-7 text-xs">Clear</Button>
            </div>
          )}
        </div>
        {(canExportPDF || canExportExcel) && (
          <div className="flex gap-2">
            {canExportPDF && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleExport('pdf')}
                disabled={exporting}
                className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs"
              >
                {exporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
                PDF
              </Button>
            )}
            {canExportExcel && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleExport('excel')}
                disabled={exporting}
                className="text-green-600 border-green-200 hover:bg-green-50 h-8 text-xs"
              >
                {exporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                Excel
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Dashboard Stats - Mobile Optimized */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-[10px] sm:text-xs text-blue-600 font-medium">Total</p>
            <p className="text-lg sm:text-2xl font-bold text-blue-700">{dashboard.total_zabhiyat || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-[10px] sm:text-xs text-green-600 font-medium">Done</p>
            <p className="text-lg sm:text-2xl font-bold text-green-700">{dashboard.zabhiyat_done || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-[10px] sm:text-xs text-orange-600 font-medium">Pending</p>
            <p className="text-lg sm:text-2xl font-bold text-orange-700">{dashboard.zabhiyat_pending || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 hidden sm:block">
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">Paid</p>
            <p className="text-lg sm:text-2xl font-bold text-emerald-700">{dashboard.total_paid || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 hidden sm:block">
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-[10px] sm:text-xs text-red-600 font-medium">Unpaid</p>
            <p className="text-lg sm:text-2xl font-bold text-red-700">{dashboard.total_unpaid || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 hidden sm:block">
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-[10px] sm:text-xs text-amber-600 font-medium">HOF Partial</p>
            <p className="text-lg sm:text-2xl font-bold text-amber-700">{dashboard.hof_partial_paid || 0}</p>
          </CardContent>
        </Card>
        {/* Done but Unpaid - Clickable for Admin */}
        <Card 
          className={`bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 ${isAdmin ? 'cursor-pointer hover:shadow-lg transition-shadow active:scale-95' : ''}`}
          onClick={() => handleDashboardClick('done_but_unpaid')}
        >
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-[10px] sm:text-xs text-purple-600 font-medium">Done Unpaid</p>
            <p className="text-lg sm:text-2xl font-bold text-purple-700">{dashboard.done_but_unpaid || 0}</p>
            {isAdmin && <p className="text-[8px] sm:text-[10px] text-purple-400 mt-0.5 hidden sm:block">Click to view</p>}
          </CardContent>
        </Card>
      </div>

      {/* Filters - Mobile Optimized */}
      <div className="space-y-2 sm:space-y-0 sm:flex sm:flex-wrap sm:gap-3 sm:items-end">
        <div className="w-full sm:flex-1 sm:min-w-[200px]">
          <Label className="text-xs text-slate-500">Search HOF</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="ITS or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
              data-testid="zabhiyat-search"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-2 sm:flex sm:gap-3">
          <div className="w-full sm:w-32">
            <Label className="text-xs text-slate-500 hidden sm:block">Mohalla</Label>
            <Select value={selectedMohalla} onValueChange={setSelectedMohalla}>
              <SelectTrigger className="h-9 text-xs sm:text-sm">
                <SelectValue placeholder="Mohalla" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mohallas</SelectItem>
                {mohallas.map((m) => (
                  <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-28">
            <Label className="text-xs text-slate-500 hidden sm:block">Sector</Label>
            <Select value={selectedSector} onValueChange={setSelectedSector}>
              <SelectTrigger className="h-9 text-xs sm:text-sm">
                <SelectValue placeholder="Sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {sectors.map((s) => (
                  <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-28">
            <Label className="text-xs text-slate-500 hidden sm:block">Payment</Label>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="h-9 text-xs sm:text-sm">
                <SelectValue placeholder="Payment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant={pendingOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setPendingOnly(!pendingOnly)}
            className="flex-1 sm:flex-none whitespace-nowrap h-9 text-xs"
          >
            {pendingOnly ? "Pending" : "Show Pending"}
          </Button>

          <Button variant="outline" size="icon" onClick={() => { fetchData(); fetchDashboard(); }} className="h-9 w-9">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-8 text-slate-500">No records found</div>
        ) : (
          data.map((item, idx) => (
            <Card key={item.hof_its} className="overflow-hidden">
              <CardContent className="p-3">
                <div className="flex justify-between items-start mb-2 gap-2">
                  <div className="flex-1">
                    <p className="font-medium text-sm leading-tight">{item.hof_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">ITS: {item.hof_its}</p>
                    <p className="text-xs text-slate-400">{item.mohalla} • {item.sector}</p>
                  </div>
                  {getPaymentBadge(item.payment_status)}
                </div>
                
                {/* Stats Row */}
                <div className="grid grid-cols-4 gap-2 mb-3 text-center">
                  <div className="bg-blue-50 rounded p-1.5">
                    <p className="text-[10px] text-blue-600">Total</p>
                    <p className="text-sm font-bold text-blue-700">{item.zabhiyat_total}</p>
                  </div>
                  <div className="bg-green-50 rounded p-1.5">
                    <p className="text-[10px] text-green-600">Done</p>
                    <p className="text-sm font-bold text-green-700">{item.zabhiyat_done}</p>
                  </div>
                  <div className="bg-orange-50 rounded p-1.5">
                    <p className="text-[10px] text-orange-600">Pending</p>
                    <p className="text-sm font-bold text-orange-700">{item.zabhiyat_pending}</p>
                  </div>
                  <div className="bg-emerald-50 rounded p-1.5">
                    <p className="text-[10px] text-emerald-600">Paid</p>
                    <p className="text-sm font-bold text-emerald-700">{item.zabhiyat_paid}</p>
                  </div>
                </div>

                {/* Actions Row */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t">
                  {/* Mobile & Call */}
                  <div className="flex items-center gap-1">
                    {item.hof_mobile && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-green-600 border-green-200"
                          onClick={() => window.location.href = `tel:${item.hof_mobile}`}
                        >
                          <Phone className="w-3.5 h-3.5 mr-1" />
                          Call
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => copyMobile(item.hof_mobile, item.hof_its)}
                        >
                          {copiedIts === item.hof_its ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4 text-slate-400" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-1">
                    {canEdit && item.zabhiyat_pending > 0 && (
                      <Button
                        size="sm"
                        className="h-8 px-3 text-xs bg-green-600 hover:bg-green-700"
                        onClick={() => { setMarkDoneDialog(item); setDoneCount(''); }}
                      >
                        Mark Done
                      </Button>
                    )}
                    {isAdmin && item.zabhiyat_done > 0 && (
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 text-orange-600 border-orange-200"
                        onClick={() => { setResetDialog(item); setResetCount(item.zabhiyat_done?.toString() || '0'); }}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {canEditPayment && (
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 text-blue-600 border-blue-200"
                        onClick={() => { setPaymentDialog(item); setPaidCount(item.zabhiyat_paid?.toString() || '0'); }}
                      >
                        <IndianRupee className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => fetchAuditLog(item.hof_its)}
                      >
                        <History className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12">Sr</TableHead>
                <TableHead>HOF ITS</TableHead>
                <TableHead>HOF Name</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Mohalla</TableHead>
                <TableHead>Sector</TableHead>
                <TableHead className="text-center">Total</TableHead>
                <TableHead className="text-center">Done</TableHead>
                <TableHead className="text-center">Pending</TableHead>
                <TableHead className="text-center">Paid</TableHead>
                <TableHead className="text-center">Payment</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                    No Zabhiyat records found
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item, idx) => (
                  <TableRow key={item.hof_its} className="hover:bg-slate-50">
                    <TableCell className="text-slate-500">{(page - 1) * 50 + idx + 1}</TableCell>
                    <TableCell className="font-mono text-sm">{item.hof_its}</TableCell>
                    <TableCell className="font-medium text-sm">
                      {item.hof_name}
                    </TableCell>
                    <TableCell>
                      {item.hof_mobile ? (
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-sm">{item.hof_mobile}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => window.location.href = `tel:${item.hof_mobile}`}
                            title="Call"
                          >
                            <Phone className="w-3 h-3 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => copyMobile(item.hof_mobile, item.hof_its)}
                            title="Copy"
                          >
                            {copiedIts === item.hof_its ? (
                              <Check className="w-3 h-3 text-green-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-slate-400" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{item.mohalla}</TableCell>
                    <TableCell className="text-sm">{item.sector}</TableCell>
                    <TableCell className="text-center font-semibold text-blue-600">{item.zabhiyat_total}</TableCell>
                    <TableCell className="text-center font-semibold text-green-600">{item.zabhiyat_done}</TableCell>
                    <TableCell className="text-center font-semibold text-orange-600">{item.zabhiyat_pending}</TableCell>
                    <TableCell className="text-center font-semibold text-emerald-600">{item.zabhiyat_paid}</TableCell>
                    <TableCell className="text-center">{getPaymentBadge(item.payment_status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        {canEdit && item.zabhiyat_pending > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 px-2 text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => { setMarkDoneDialog(item); setDoneCount(''); }}
                            data-testid={`mark-done-${item.hof_its}`}
                          >
                            Mark Done
                          </Button>
                        )}
                        {isAdmin && item.zabhiyat_done > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 px-2 text-orange-600 border-orange-200 hover:bg-orange-50"
                            onClick={() => { setResetDialog(item); setResetCount(item.zabhiyat_done?.toString() || '0'); }}
                            title="Edit/Reset Done Count"
                            data-testid={`reset-done-${item.hof_its}`}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        )}
                        {canEditPayment && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => { setPaymentDialog(item); setPaidCount(item.zabhiyat_paid?.toString() || '0'); }}
                            data-testid={`update-payment-${item.hof_its}`}
                          >
                            <IndianRupee className="w-3 h-3" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-7 px-2"
                            onClick={() => fetchAuditLog(item.hof_its)}
                            title="View History"
                          >
                            <History className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs sm:text-sm text-slate-500">
            {(page - 1) * 50 + 1} - {Math.min(page * 50, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs sm:text-sm min-w-[80px] text-center">{page} / {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Mark Done Dialog */}
      <Dialog open={!!markDoneDialog} onOpenChange={() => setMarkDoneDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Zabhiyat Done</DialogTitle>
          </DialogHeader>
          {markDoneDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{markDoneDialog.hof_name}</p>
                <p className="text-sm text-slate-500">ITS: {markDoneDialog.hof_its}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span>Total: <strong>{markDoneDialog.zabhiyat_total}</strong></span>
                  <span>Done: <strong className="text-green-600">{markDoneDialog.zabhiyat_done}</strong></span>
                  <span>Pending: <strong className="text-orange-600">{markDoneDialog.zabhiyat_pending}</strong></span>
                </div>
              </div>
              <div>
                <Label>How many Zabhiyat completed?</Label>
                <Input
                  type="number"
                  min="1"
                  max={markDoneDialog.zabhiyat_pending}
                  value={doneCount}
                  onChange={(e) => setDoneCount(e.target.value)}
                  placeholder={`Max: ${markDoneDialog.zabhiyat_pending}`}
                  data-testid="done-count-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleMarkDone} disabled={saving || !doneCount}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog (Admin) */}
      <Dialog open={!!paymentDialog} onOpenChange={() => setPaymentDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Zabhiyat Payment</DialogTitle>
          </DialogHeader>
          {paymentDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{paymentDialog.hof_name}</p>
                <p className="text-sm text-slate-500">ITS: {paymentDialog.hof_its}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span>Total: <strong>{paymentDialog.zabhiyat_total}</strong></span>
                  <span>Current Paid: <strong className="text-emerald-600">{paymentDialog.zabhiyat_paid}</strong></span>
                </div>
              </div>
              <div>
                <Label>Number of Zabhiyat Paid</Label>
                <Input
                  type="number"
                  min="0"
                  max={paymentDialog.zabhiyat_total}
                  value={paidCount}
                  onChange={(e) => setPaidCount(e.target.value)}
                  placeholder={`0 - ${paymentDialog.zabhiyat_total}`}
                  data-testid="paid-count-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {parseInt(paidCount) === 0 ? 'Status: Unpaid' : 
                   parseInt(paidCount) >= paymentDialog.zabhiyat_total ? 'Status: Paid' : 'Status: Partial'}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleUpdatePayment} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog (Admin) */}
      <Dialog open={!!auditDialog} onOpenChange={() => setAuditDialog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Zabhiyat Audit Log</DialogTitle>
          </DialogHeader>
          {auditDialog && (
            <div className="space-y-3">
              <div className="bg-slate-50 p-2 rounded">
                <p className="font-medium text-sm">{auditDialog.hof_name}</p>
                <p className="text-xs text-slate-500">ITS: {auditDialog.hof_its}</p>
              </div>
              {auditHistory.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No history found</p>
              ) : (
                <div className="space-y-2">
                  {auditHistory.map((entry, idx) => (
                    <div key={idx} className="border rounded p-2 text-sm">
                      <div className="flex justify-between">
                        <Badge variant={entry.action === 'mark_done' ? 'default' : 'secondary'}>
                          {entry.action === 'mark_done' ? 'Marked Done' : 'Payment Update'}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(entry.updated_at).toLocaleString()}
                        </span>
                      </div>
                      {entry.action === 'mark_done' ? (
                        <p className="mt-1">
                          Added <strong>{entry.done_added}</strong> done 
                          ({entry.previous_done} → {entry.new_done})
                        </p>
                      ) : entry.action === 'reset_done' ? (
                        <p className="mt-1">
                          Reset done: <strong>{entry.previous_done}</strong> → <strong>{entry.new_done}</strong>
                        </p>
                      ) : (
                        <p className="mt-1">
                          Payment: <strong>{entry.previous_paid}</strong> → <strong>{entry.new_paid}</strong>
                          <Badge className="ml-2" variant="outline">{entry.payment_status}</Badge>
                        </p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        By: {entry.updated_by_name} ({entry.updated_by_its})
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset/Edit Done Dialog (Admin) */}
      <Dialog open={!!resetDialog} onOpenChange={() => setResetDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Zabhiyat Done Count</DialogTitle>
          </DialogHeader>
          {resetDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{resetDialog.hof_name}</p>
                <p className="text-sm text-slate-500">ITS: {resetDialog.hof_its}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span>Total: <strong>{resetDialog.zabhiyat_total}</strong></span>
                  <span>Current Done: <strong className="text-green-600">{resetDialog.zabhiyat_done}</strong></span>
                </div>
              </div>
              <div>
                <Label>New Done Count</Label>
                <Input
                  type="number"
                  min="0"
                  max={resetDialog.zabhiyat_total}
                  value={resetCount}
                  onChange={(e) => setResetCount(e.target.value)}
                  placeholder={`0 - ${resetDialog.zabhiyat_total}`}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Set to 0 to reset completely
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleResetDone} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Unpaid Dialog */}
      <Dialog open={!!confirmUnpaidDialog} onOpenChange={() => setConfirmUnpaidDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Payment Pending
            </DialogTitle>
            <DialogDescription>
              This HOF has not paid for Zabhiyat yet. Are you sure you want to mark as done?
            </DialogDescription>
          </DialogHeader>
          {confirmUnpaidDialog && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
              <p className="font-medium">{confirmUnpaidDialog.item.hof_name}</p>
              <p className="text-sm text-slate-500">Payment Status: <span className="text-red-600 font-medium">Unpaid</span></p>
              <p className="text-sm mt-1">Marking <strong>{confirmUnpaidDialog.count}</strong> Zabhiyat as done</p>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={() => executeMarkDone(confirmUnpaidDialog.item.hof_its, confirmUnpaidDialog.count)}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Continue Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
